import { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls, useInView, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { Reveal, Section, Overline } from "@/lib/motion";
import useMotionPreference from "@/hooks/useMotionPreference";
import useScrollStage from "@/hooks/useScrollStage";
import { ABOUT_STORY } from "../../data/aboutData";

export default function About() {
  const storyRef = useRef(null);
  const portraitRef = useRef(null);
  const reduceMotion = useMotionPreference();
  const sticky = useScrollStage();
  const inView = useInView(portraitRef, { once: true, amount: 0.2 });
  const portraitControls = useAnimationControls();
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: storyRef, offset: ["start 55%", "end 60%"] });
  const photoScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.08]);
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "-3%"]);
  const grayscaleOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  useMotionValueEvent(scrollYProgress, "change", value => setActive(Math.min(ABOUT_STORY.length - 1, Math.floor(value * ABOUT_STORY.length))));
  useEffect(() => {
    if (reduceMotion) {
      portraitControls.stop();
      portraitControls.set({ clipPath: "inset(0% 0% 0% 0%)" });
    } else if (inView) {
      portraitControls.start({ clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } });
    }
  }, [inView, reduceMotion, portraitControls]);

  return (
    <Section id="about" className="py-16 md:py-32">
      <Overline num="01">About</Overline>
      <div className="grid grid-cols-12 items-start gap-y-10 lg:gap-12">
        <div className="col-span-12 flex justify-center lg:col-span-5 lg:block" style={sticky ? { position: "sticky", top: 112 } : undefined} data-testid="about-portrait-column">
          <div ref={portraitRef} className="w-full max-w-[260px] sm:max-w-sm lg:max-w-none">
            <motion.div
              data-testid="about-portrait-mask"
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] shadow-2xl"
              initial={reduceMotion ? false : { clipPath: "inset(8% 48% 8% 48%)" }}
              animate={portraitControls}
            >
              <motion.div className="relative aspect-[4/5] w-full overflow-hidden" style={{ scale: sticky && !reduceMotion ? photoScale : 1, y: sticky && !reduceMotion ? photoY : 0 }}>
                <img src="/tanishq.png" alt="Tanishq Saini" loading="lazy" className="h-full w-full object-cover" />
                <motion.img src="/tanishq.png" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover grayscale" style={{ opacity: sticky && !reduceMotion ? grayscaleOpacity : 0 }} />
              </motion.div>
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 font-mono leading-snug">
                <p className="text-xs font-semibold text-white sm:text-sm">Tanishq Saini</p>
                <p className="mt-0.5 text-[10px] text-white/70 sm:text-xs">BCA @ Christ University (3.7/4 GPA · Jun 2027)</p>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="col-span-12 min-w-0 lg:col-span-7">
          <Reveal><h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl lg:text-5xl">I design and engineer software that feels <span className="text-brand-400">effortless</span>.</h2></Reveal>
          <div ref={storyRef} className="relative mt-10 border-l border-white/10 pl-6 sm:pl-8">
            <motion.div aria-hidden="true" data-testid="about-story-rail" className="absolute -left-px inset-y-0 w-px origin-top bg-brand-500" style={{ scaleY: reduceMotion ? 1 : scrollYProgress }} />
            {ABOUT_STORY.map((point, index) => (
              <article key={point.n} data-about-story={index} data-active={active === index} className="relative flex gap-4 py-7 sm:gap-5 lg:min-h-[240px] lg:items-center">
                <span aria-hidden="true" className={`absolute -left-[29px] top-9 h-2 w-2 rounded-full border border-brand-400 transition-colors duration-300 sm:-left-[37px] lg:top-1/2 ${reduceMotion || index <= active ? "bg-brand-400" : "bg-[#050505]"}`} />
                <span className={`self-start pt-0.5 font-mono text-xs transition-colors duration-300 sm:text-sm lg:self-auto ${active === index ? "text-brand-300" : "text-white/35"}`}>{point.n}</span>
                <div className="min-w-0">
                  <div className="mb-3 flex items-center gap-2"><point.icon aria-hidden="true" className="h-4 w-4 shrink-0 text-brand-400" /><h3 className="font-display text-base font-semibold text-white sm:text-lg">{point.title}</h3></div>
                  <p className="text-sm leading-relaxed text-white/65">{point.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
